import { useState, useEffect, useCallback } from 'preact/hooks'
import type { CloudNote } from '../../types'
import { listCloudNotes, deleteCloudNote, downloadBlobAsText, listBlobKeys } from '../../services/api'
import { formatDate, EmptyState, DetailRow, listAreaStyle, refreshBtnStyle, slugifyFilename, triggerDownloadText } from './shared'
import { ConfirmDialog, DeleteButton } from './CrudHelpers'

/** Pull the app's emoji from the first HTML attachment's metadata, falling back to a generic icon. */
function appEmoji(note: CloudNote): string {
  const atts = note.attachments
  if (!Array.isArray(atts)) return '\u{1F9E9}'
  for (const att of atts) {
    const p = (att.file_path || att.filePath || '').toLowerCase()
    if (!p.endsWith('.html') && !p.endsWith('.htm')) continue
    const meta = (att as { metadata?: Record<string, unknown> }).metadata
    const emoji = meta?.emoji
    if (typeof emoji === 'string' && emoji) return emoji
  }
  return '\u{1F9E9}'
}

/** App display name — attachment metadata wins, then the note title. */
function appName(note: CloudNote): string {
  const atts = note.attachments
  if (Array.isArray(atts)) {
    for (const att of atts) {
      const p = (att.file_path || att.filePath || '').toLowerCase()
      if (!p.endsWith('.html') && !p.endsWith('.htm')) continue
      const meta = (att as { metadata?: Record<string, unknown> }).metadata
      const name = meta?.app_name
      if (typeof name === 'string' && name.trim()) return name.trim()
    }
  }
  return note.title?.trim() || 'Untitled App'
}

/** Normalize camelCase fields from Flutter to snake_case used by the web client */
function normalizeNote(n: CloudNote): CloudNote {
  const r = n as any
  if (r.isApp != null && n.is_app == null) n.is_app = r.isApp
  if (r.createdAt != null && n.created_at == null) n.created_at = r.createdAt
  if (r.lastUpdatedAt != null && n.last_updated_at == null) n.last_updated_at = r.lastUpdatedAt
  if (n.attachments) {
    for (const att of n.attachments) {
      if (att.blobKey && !att.blob_key) att.blob_key = att.blobKey
      if (att.filePath && !att.file_path) att.file_path = att.filePath
    }
  }
  return n
}

/** Resolve cloud blob key: check attachments first, then list from backend */
async function resolveBlobKey(note: CloudNote): Promise<string | null> {
  if (!Array.isArray(note.attachments) || note.attachments.length === 0) return null
  for (const att of note.attachments) {
    // Check blobKey (camelCase from Flutter) and blob_key (snake_case legacy)
    const bk = att.blobKey || att.blob_key
    if (bk) return bk as string
    // Fallback: check if filePath is itself a cloud blob key
    const key = att.file_path || att.filePath
    if (!key) continue
    const parts = key.split('/')
    if (parts.length >= 4 && /^[0-9a-f]{8,}$/.test(parts[0])) return key
  }
  // Attachment has local-only path — ask backend
  const keys = await listBlobKeys('notes', note.id)
  return keys.length > 0 ? keys[0] : null
}

export function AppsView() {
  const [notes, setNotes] = useState<CloudNote[]>([])
  const [loading, setLoading] = useState(true)
  const [selectedApp, setSelectedApp] = useState<CloudNote | null>(null)
  const [confirmDeleteApp, setConfirmDeleteApp] = useState<CloudNote | null>(null)
  const [deleting, setDeleting] = useState(false)
  const [deleteError, setDeleteError] = useState<string | null>(null)

  const handleDelete = useCallback(async () => {
    if (!confirmDeleteApp || deleting) return
    setDeleting(true)
    setDeleteError(null)
    try {
      await deleteCloudNote(confirmDeleteApp.id)
      setNotes((prev) => prev.filter((n) => n.id !== confirmDeleteApp.id))
      if (selectedApp?.id === confirmDeleteApp.id) setSelectedApp(null)
      setConfirmDeleteApp(null)
    } catch (e) {
      setDeleteError((e as Error).message)
    } finally {
      setDeleting(false)
    }
  }, [confirmDeleteApp, deleting, selectedApp])

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const all = (await listCloudNotes()).map(normalizeNote)
      setNotes(all.filter((n) => n.is_app === true))
    } catch (e) {
      console.error('Failed to load apps:', e)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { load() }, [load])

  // When an app is open it takes over the entire main area — tiles
  // disappear behind it. The panel's back button returns to the launcher.
  if (selectedApp) {
    return (
      <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
        <AppDetailPanel
          app={selectedApp}
          onClose={() => setSelectedApp(null)}
          onDelete={() => setConfirmDeleteApp(selectedApp)}
        />
        {confirmDeleteApp && (
          <ConfirmDialog
            message={`Delete app "${confirmDeleteApp.title || 'Untitled App'}"? This cannot be undone.`}
            confirmLabel={deleting ? 'Deleting…' : 'Delete'}
            onConfirm={handleDelete}
            onCancel={() => { if (!deleting) { setConfirmDeleteApp(null); setDeleteError(null) } }}
          />
        )}
        {deleteError && (
          <div style={{ position: 'fixed', bottom: 16, right: 16, padding: '10px 14px', borderRadius: 8, background: '#f44336', color: '#fff', fontSize: 13, zIndex: 1100 }}>
            Delete failed: {deleteError}
          </div>
        )}
      </div>
    )
  }

  return (
    <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
      <div style={listAreaStyle}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '12px 16px', borderBottom: '1px solid var(--border)' }}>
          <span style={{ flex: 1, fontSize: 13, color: 'var(--text-muted)' }}>{notes.length} apps</span>
          <button onClick={load} style={refreshBtnStyle} disabled={loading}>
            {loading ? '...' : 'Refresh'}
          </button>
        </div>

        <div style={{ flex: 1, overflow: 'auto', padding: 16 }}>
          {loading && <div style={{ padding: 24, textAlign: 'center', color: 'var(--text-muted)' }}>Loading...</div>}
          {!loading && notes.length === 0 && <EmptyState label="No apps yet — AI-generated mini-apps will land here." />}
          {!loading && notes.length > 0 && (
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))',
                gap: 12,
              }}
            >
              {notes.map((note) => (
                <AppTile key={note.id} note={note} onOpen={() => setSelectedApp(note)} />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

/** A single launcher tile — emoji on top, title below. */
function AppTile({ note, onOpen }: { note: CloudNote; onOpen: () => void }) {
  const [hover, setHover] = useState(false)
  const emoji = appEmoji(note)
  const name = appName(note)
  return (
    <div
      onClick={onOpen}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        cursor: 'pointer',
        background: hover ? 'rgba(45,165,160,0.08)' : 'var(--bg-surface)',
        border: `1px solid ${hover ? 'rgba(45,165,160,0.4)' : 'var(--border)'}`,
        borderRadius: 12,
        boxShadow: hover ? '0 4px 12px rgba(45,165,160,0.12)' : 'none',
        padding: 12,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: 140,
        transition: 'background 120ms, border-color 120ms, box-shadow 120ms',
      }}
      title={name}
    >
      <span style={{ fontSize: 52, lineHeight: 1, marginBottom: 8 }}>{emoji}</span>
      <span
        style={{
          fontSize: 12,
          fontWeight: 500,
          color: 'var(--text)',
          textAlign: 'center',
          maxWidth: '100%',
          overflow: 'hidden',
          display: '-webkit-box',
          WebkitLineClamp: 2,
          WebkitBoxOrient: 'vertical',
          textOverflow: 'ellipsis',
          wordBreak: 'break-word',
        }}
      >
        {name}
      </span>
    </div>
  )
}

/** App detail panel that loads HTML content from blob storage or inline */
function AppDetailPanel({ app, onClose, onDelete }: { app: CloudNote; onClose: () => void; onDelete?: () => void }) {
  const [htmlContent, setHtmlContent] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    setHtmlContent(null)
    setLoading(true)

    // Always try blob first — app.content is usually just a description, not the HTML script
    resolveBlobKey(app).then((key) => {
      if (key) {
        downloadBlobAsText(key).then((text) => {
          if (text) setHtmlContent(text)
          else if (app.content) setHtmlContent(app.content)
          setLoading(false)
        })
      } else if (app.content) {
        // No blob — fall back to inline content
        setHtmlContent(app.content)
        setLoading(false)
      } else {
        setLoading(false)
      }
    })
  }, [app.id])

  const name = appName(app)
  const emoji = appEmoji(app)

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', background: 'var(--bg-surface)' }}>
      <div style={{ padding: 12, borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: 8 }}>
        <button
          onClick={onClose}
          style={{ ...refreshBtnStyle, padding: '4px 8px' }}
          title="Back to apps"
        >
          ← Back
        </button>
        <span style={{ fontSize: 22 }}>{emoji}</span>
        <span style={{ flex: 1, fontWeight: 600, fontSize: 16 }}>{name}</span>
        {htmlContent && (
          <button
            onClick={() => {
              const blob = new Blob([htmlContent], { type: 'text/html' })
              window.open(URL.createObjectURL(blob), '_blank')
            }}
            style={refreshBtnStyle}
          >
            Open in new tab
          </button>
        )}
        {htmlContent && (
          <button
            onClick={() => {
              triggerDownloadText(htmlContent, 'text/html', slugifyFilename(name, 'html'))
            }}
            style={refreshBtnStyle}
            title="Download HTML to your computer"
          >
            Export
          </button>
        )}
        {onDelete && <DeleteButton onClick={onDelete} />}
      </div>
      <div style={{ flex: 1, overflow: 'auto', padding: 16 }}>
        {loading && (
          <div style={{ padding: 40, textAlign: 'center', color: 'rgba(250,250,250,0.4)' }}>Loading app content...</div>
        )}
        {!loading && htmlContent ? (
          <iframe
            srcDoc={htmlContent}
            sandbox="allow-scripts"
            style={{
              width: '100%',
              height: 'calc(100dvh - 200px)',
              border: '1px solid rgba(250,250,250,0.1)',
              borderRadius: 8,
              background: '#fff',
            }}
            title={app.title || 'App'}
          />
        ) : !loading && !htmlContent ? (
          <div style={{ color: 'rgba(250,250,250,0.4)', fontSize: 13 }}>No content available</div>
        ) : null}
        <div style={{ marginTop: 16, paddingTop: 12, borderTop: '1px solid rgba(250,250,250,0.1)' }}>
          {Array.isArray(app.tags) && app.tags.length > 0 && <DetailRow label="Tags" value={app.tags.join(', ')} />}
          {app.attachments && app.attachments.length > 0 && (
            <DetailRow label="Attachments" value={`${app.attachments.length} file(s)`} />
          )}
          <DetailRow label="Created" value={formatDate(app.created_at)} />
          <DetailRow label="Updated" value={formatDate(app.last_updated_at)} />
        </div>
      </div>
    </div>
  )
}
